import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, ArrowRight, Home } from 'lucide-react';
import { useDarkMode } from '../contexts/DarkModeContext';
import { getOfflineSessionIdForShare } from '../services/offlineSessionStore';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';

const isDevelopment = process.env.NODE_ENV === 'development';
const API_BASE_URL = isDevelopment ? 'http://localhost:7777' : '';
const PUBLIC_SHARE_LOAD_TIMEOUT_MS = 12000;

interface ShareLinkPermissions {
  sessionId: string;
  transcriptsEnabled: boolean;
  timelineEnabled: boolean;
  rankingEnabled: boolean;
  rankingTtmlAllowed: boolean;
  chatMode: 'DISABLED' | 'READ_ONLY' | 'FULL';
  llmCallCap: number;
  commentsRead: boolean;
  commentsWrite: boolean;
  isPublicShare: boolean;
  contextFilesEnabled?: boolean;
}

interface ShareLinkData {
  label?: string;
  expiresAt?: string;
}

interface SessionData {
  session_id: string;
  filename: string;
  is_youtube_mode: boolean;
  youtube_url?: string;
  final_transcript: string;
  current_transcript: string;
  speaker_segments: any[];
  distinct_speakers: string[];
  s3_original_audio_key?: string;
  title?: string;
  created_at: string;
  updated_at: string;
}

interface PublicShareData {
  session: SessionData;
  permissions: ShareLinkPermissions;
  shareLink: ShareLinkData;
}

const PublicShareView: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useDarkMode();
  const [error, setError] = useState<string>('');
  const forwardedSearch = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const forwarded = new URLSearchParams();
    const timeValue = params.get('t');
    if (timeValue) {
      forwarded.set('t', timeValue);
    }
    const query = forwarded.toString();
    return query ? `&${query}` : '';
  }, [location.search]);

  const loadShareData = useCallback(async () => {
    setError('');

    const tryOpenOfflineShare = async (): Promise<boolean> => {
      if (!shareToken) {
        return false;
      }

      const offlineSessionId = await getOfflineSessionIdForShare(shareToken);
      if (!offlineSessionId) {
        return false;
      }

      navigate(`/session/${offlineSessionId}?share=${shareToken}${forwardedSearch}`, { replace: true });
      return true;
    };

    try {
      if (!navigator.onLine && await tryOpenOfflineShare()) {
        return;
      }

      const response = await fetchWithTimeout(
        `${API_BASE_URL}/api/public/share/${shareToken}`,
        { cache: 'no-store', credentials: 'omit' },
        PUBLIC_SHARE_LOAD_TIMEOUT_MS,
        'Timed out while loading the shared session'
      );

      if (!response.ok) {
        if (response.status === 404) {
          setError('Share link not found');
        } else if (response.status === 410) {
          setError('This share link is not currently available');
        } else if (response.status === 403) {
          setError('This share link has been disabled or reached its viewer limit');
        } else {
          setError('Failed to load shared session');
        }
        return;
      }

      const data: PublicShareData = await response.json();

      await fetch(`${API_BASE_URL}/api/public/share/${shareToken}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify({ event: 'TAB_VIEW', metadata: { tab: 'initial' } }),
      }).catch(console.error);

      navigate(`/session/${data.session.session_id}?share=${shareToken}${forwardedSearch}`, { replace: true });
    } catch (err) {
      console.error('[PUBLIC-SHARE] Error loading data:', err);
      if (await tryOpenOfflineShare()) {
        return;
      }

      setError('Failed to load shared session');
    }
  }, [forwardedSearch, navigate, shareToken]);

  useEffect(() => {
    if (shareToken) {
      void loadShareData();
    }
  }, [loadShareData, shareToken]);

  return (
    <div className={isDarkMode ? 'dark' : undefined}>
      <div className="brand-shell brand-grid">
        <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="brand-card w-full max-w-xl p-8 text-center sm:p-10"
          >
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-[1.35rem] bg-brand-navy shadow-brand dark:bg-white/10">
              <img
                src="/logos/combination_mark_yellow.svg"
                alt="Machine Learning Street Talk"
                className="h-8 w-auto"
              />
            </div>

            {error ? (
              <>
                <p className="mt-8 brand-kicker">Public Share</p>
                <div className="mx-auto mt-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-600 dark:bg-red-500/12 dark:text-red-300">
                  <AlertCircle className="h-8 w-8" />
                </div>
                <h1 className="mt-6 font-headlines text-4xl font-bold tracking-tight text-brand-navy dark:text-brand-cloud sm:text-5xl">
                  {error}
                </h1>
                <p className="mt-4 text-base leading-8 text-brand-navy/68 dark:text-brand-cloud/72">
                  The shared transcript is not currently available. The link may be disabled or no longer public.
                </p>
                <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <button onClick={() => navigate('/')} className="brand-button-primary">
                    <Home className="h-4 w-4" />
                    Back to archive
                  </button>
                  <button onClick={() => void loadShareData()} className="brand-button-secondary">
                    Try again
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="mt-8 brand-kicker">Public Share</p>
                <div className="mx-auto mt-6 brand-spinner" />
                <h1 className="mt-6 font-headlines text-4xl font-bold tracking-tight text-brand-navy dark:text-brand-cloud sm:text-5xl">
                  Opening shared transcript
                </h1>
                <p className="mt-4 text-base leading-8 text-brand-navy/68 dark:text-brand-cloud/72">
                  We are validating the share link and redirecting you into the MLST transcript view.
                </p>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PublicShareView;
